import { logger, sanitizeError } from '@heidi/runtime/logger';
import { type components } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_USER } from '../../contracts/keys';
import { type UserResponse } from '../../contracts/types/user-types';
import { submitSalesBookingForm } from '../api/user';

export type SalesBookingFormName = components['schemas']['SalesBookingFormName'];
export type SalesBookingSubmissionRequest =
  components['schemas']['SalesBookingSubmissionRequest'];
export type SalesBookingSubmissionResponse =
  components['schemas']['SalesBookingSubmissionResponse'];

export type UseMutateSubmitSalesBookingFormVariables = {
  formName: SalesBookingFormName;
  body: SalesBookingSubmissionRequest;
};

export interface UseMutateSubmitSalesBookingFormOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onSettled?: () => void | Promise<void>;
}

function applyOptimisticSalesBookingSubmission(
  user: UserResponse,
  formName: SalesBookingFormName
): UserResponse {
  const existing = user.sales_booking_forms_submitted?.[formName];
  const now = new Date().toISOString();
  return {
    ...user,
    sales_booking_forms_submitted: {
      ...user.sales_booking_forms_submitted,
      [formName]: {
        last_submitted_at: now,
        submit_count: (existing?.submit_count ?? 0) + 1,
      },
    },
  };
}

/**
 * POST /sales-booking/{form_name}. Optimistically updates
 * `sales_booking_forms_submitted` and rolls it back on error.
 */
export function useMutateSubmitSalesBookingForm(
  options?: UseMutateSubmitSalesBookingFormOptions
) {
  const queryClient = useQueryClient();

  return useMutation<
    SalesBookingSubmissionResponse,
    Error,
    UseMutateSubmitSalesBookingFormVariables,
    { previousUser?: UserResponse }
  >({
    mutationFn: async ({ formName, body }) => {
      const { data } = await submitSalesBookingForm(formName, body);
      return data;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER] });

      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);

      if (previousUser) {
        queryClient.setQueryData<UserResponse>(
          [GET_USER],
          applyOptimisticSalesBookingSubmission(previousUser, variables.formName)
        );
      }

      return { previousUser };
    },
    onError: (error, variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
      }
      logger.error(
        `Failed to submit sales booking form '${variables.formName}'`,
        sanitizeError(error)
      );
    },
    onSettled: async () => {
      await options?.onSettled?.();
    },
  });
}
